import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Avatar,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton,
  Badge,
  TextField,
  InputAdornment,
  TablePagination,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Block as BlockIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Print as PrintIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useLocation } from 'react-router-dom';
import apiService from '../../services/api';
import { CheckIn } from '../../types';

interface TodaysCheckInsProps {
  refreshTrigger?: number; // Used to trigger refresh from parent
}

interface PopulatedMember {
  _id: string;
  firstName: string;
  lastName: string;
  memberId: string;
  profileImage?: string;
  membershipType: string;
  membershipStatus: string;
}

interface CheckInWithMember extends Omit<CheckIn, 'member'> {
  member: PopulatedMember;
}

const TodaysCheckIns: React.FC<TodaysCheckInsProps> = ({ refreshTrigger = 0 }) => {
  const [checkIns, setCheckIns] = useState<CheckInWithMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  
  // Search and pagination state
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Get URL parameters for date filtering
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const startDate = urlParams.get('startDate');
  const endDate = urlParams.get('endDate');
  const statusFilter = urlParams.get('status');

  // Utility functions (moved before useMemo hooks)
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircleIcon sx={{ fontSize: 20, color: 'success.main' }} />;
      case 'contract-expired':
        return <ErrorIcon sx={{ fontSize: 20, color: 'error.main' }} />;
      case 'payment-overdue':
        return <WarningIcon sx={{ fontSize: 20, color: 'warning.main' }} />;
      case 'suspended':
        return <BlockIcon sx={{ fontSize: 20, color: 'error.main' }} />;
      default:
        return <CheckCircleIcon sx={{ fontSize: 20, color: 'grey.500' }} />;
    }
  };

  const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
    switch (status) {
      case 'success':
        return 'success';
      case 'contract-expired':
      case 'suspended':
        return 'error';
      case 'payment-overdue':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'success':
        return 'Access Granted';
      case 'contract-expired':
        return 'Expired';
      case 'payment-overdue':
        return 'Payment Due';
      case 'suspended':
        return 'Suspended';
      default:
        return 'Unknown';
    }
  };

  const getMembershipTypeColor = (type: string) => {
    switch (type.toLowerCase()) {
      case 'home owner (h/o)':
        return '#FFD700';
      case 'outside member':
        return '#8A2BE2';
      case 'tenant':
        return '#4CAF50';
      default:
        return '#9E9E9E';
    }
  };

  // Filter and paginate data
  const filteredCheckIns = useMemo(() => {
    if (!searchTerm.trim()) return checkIns;
    
    return checkIns.filter(checkIn => {
      const searchLower = searchTerm.toLowerCase();
      const fullName = `${checkIn.member.firstName} ${checkIn.member.lastName}`.toLowerCase();
      const memberId = checkIn.member.memberId.toLowerCase();
      const membershipType = checkIn.member.membershipType.toLowerCase();
      const status = getStatusLabel(checkIn.status).toLowerCase();
      
      return fullName.includes(searchLower) ||
             memberId.includes(searchLower) ||
             membershipType.includes(searchLower) ||
             status.includes(searchLower);
    });
  }, [checkIns, searchTerm]);

  const paginatedCheckIns = useMemo(() => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredCheckIns.slice(startIndex, endIndex);
  }, [filteredCheckIns, page, rowsPerPage]);

  // Count successful vs failed check-ins from filtered results
  const successfulCheckIns = filteredCheckIns.filter(checkIn => checkIn.status === 'success').length;
  const failedCheckIns = filteredCheckIns.length - successfulCheckIns;

  const fetchCheckIns = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use date filtering if URL parameters are provided, otherwise default to today
      if (startDate && endDate) {
        const params: any = {
          startDate,
          endDate,
          limit: 1000 // Get all check-ins for the date range
        };
        
        // Add status filter if provided
        if (statusFilter) {
          params.status = statusFilter;
        }
        
        const response = await apiService.getCheckIns(params);
        setCheckIns(response.checkIns || []);
      } else {
        // Default to today's check-ins
        const response = await apiService.getTodaysCheckIns();
        let checkInsData = response.checkIns;
        
        // Apply status filter to today's data if provided
        if (statusFilter && checkInsData) {
          checkInsData = checkInsData.filter((checkIn: any) => checkIn.status === statusFilter);
        }
        
        setCheckIns(checkInsData);
      }
      
      setLastRefresh(new Date());
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load check-ins');
      console.error('Error fetching check-ins:', err);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, statusFilter]);

  useEffect(() => {
    fetchCheckIns();
  }, [refreshTrigger, fetchCheckIns]);

  const handleManualRefresh = () => {
    fetchCheckIns();
  };

  // Search functionality
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0); // Reset to first page when searching
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setPage(0);
  };

  // Pagination handlers
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Print functionality
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const printContent = generatePrintContent();
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const generatePrintContent = () => {
    const today = format(new Date(), 'EEEE, MMMM do, yyyy');
    const printTime = format(new Date(), 'HH:mm');
    
    return `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Daily Check-ins Report - ${today}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 10px; }
            .stats { margin-bottom: 20px; display: flex; justify-content: space-between; }
            .stats div { padding: 10px; background: #f5f5f5; border-radius: 4px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; font-weight: bold; }
            .success { color: #4caf50; }
            .error { color: #f44336; }
            .warning { color: #ff9800; }
            .footer { margin-top: 30px; text-align: center; font-size: 12px; color: #666; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Daily Check-ins Report</h1>
            <h2>${today}</h2>
            <p>Generated at ${printTime}</p>
          </div>
          
          <div class="stats">
            <div><strong>Total Check-ins:</strong> ${checkIns.length}</div>
            <div><strong>Successful:</strong> <span class="success">${successfulCheckIns}</span></div>
            <div><strong>Failed:</strong> <span class="error">${failedCheckIns}</span></div>
          </div>

          <table>
            <thead>
              <tr>
                <th>Member Name</th>
                <th>Member ID</th>
                <th>Check-in Time</th>
                <th>Status</th>
                <th>Membership Type</th>
              </tr>
            </thead>
            <tbody>
              ${checkIns.map(checkIn => `
                <tr>
                  <td>${checkIn.member.firstName} ${checkIn.member.lastName}</td>
                  <td>${checkIn.member.memberId}</td>
                  <td>${format(new Date(checkIn.timestamp), 'HH:mm:ss')}</td>
                  <td class="${getStatusColor(checkIn.status)}">${getStatusLabel(checkIn.status)}</td>
                  <td>${checkIn.member.membershipType}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>

          <div class="footer">
            <p>Report generated by Legends Gym Management System</p>
          </div>
        </body>
      </html>
    `;
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="h6">
              {startDate && endDate ? `Check-ins (${format(new Date(startDate), 'MMM dd')} - ${format(new Date(endDate), 'MMM dd')})` : "Today's Check-ins"}
            </Typography>
            <Badge 
              badgeContent={failedCheckIns} 
              color="error" 
              invisible={failedCheckIns === 0}
              sx={{ '& .MuiBadge-badge': { fontSize: '0.7rem' } }}
            >
              <Chip 
                label={`${filteredCheckIns.length} total`} 
                size="small" 
                color="primary" 
                variant="outlined" 
              />
            </Badge>
            {successfulCheckIns > 0 && (
              <Chip 
                label={`${successfulCheckIns} successful`} 
                size="small" 
                color="success" 
                variant="outlined" 
              />
            )}
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="caption" color="text.secondary">
              Last updated: {format(lastRefresh, 'HH:mm')}
            </Typography>
            <Tooltip title="Print report">
              <IconButton 
                size="small" 
                onClick={handlePrint}
                disabled={checkIns.length === 0}
              >
                <PrintIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Refresh check-ins">
              <IconButton 
                size="small" 
                onClick={handleManualRefresh}
                disabled={loading}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Search Bar */}
        <Box mb={2}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by member name, ID, membership type, or status..."
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleClearSearch}>
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {checkIns.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Typography variant="body1" color="text.secondary">
              No check-ins recorded today yet
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={1}>
              Members will appear here when they sign in at the gym
            </Typography>
          </Box>
        ) : filteredCheckIns.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Typography variant="body1" color="text.secondary">
              No check-ins match your search criteria
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={1}>
              Try adjusting your search terms
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'background.default' }}>
                    <TableCell><strong>Member</strong></TableCell>
                    <TableCell><strong>Time</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Membership</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedCheckIns.map((checkIn) => (
                    <TableRow 
                      key={checkIn._id} 
                      hover
                      sx={{
                        bgcolor: checkIn.status !== 'success' ? 'error.light' : 'inherit',
                        '&:hover': {
                          bgcolor: checkIn.status !== 'success' ? 'error.main' : 'action.hover',
                        }
                      }}
                    >
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Avatar
                            src={checkIn.member.profileImage || undefined}
                            sx={{ width: 32, height: 32 }}
                          >
                            {checkIn.member.firstName[0]}{checkIn.member.lastName[0]}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {checkIn.member.firstName} {checkIn.member.lastName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {checkIn.member.memberId}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {format(new Date(checkIn.timestamp), 'HH:mm')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {format(new Date(checkIn.timestamp), 'dd/MM')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Tooltip title={getStatusLabel(checkIn.status)}>
                            {getStatusIcon(checkIn.status)}
                          </Tooltip>
                          <Chip
                            label={getStatusLabel(checkIn.status)}
                            color={getStatusColor(checkIn.status)}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={checkIn.member.membershipType}
                          size="small"
                          sx={{
                            bgcolor: getMembershipTypeColor(checkIn.member.membershipType),
                            color: 'white',
                            fontWeight: 'bold',
                            textTransform: 'capitalize'
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <TablePagination
              component="div"
              count={filteredCheckIns.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              sx={{ mt: 1 }}
            />
          </>
        )}

        {/* Summary section for quick stats */}
        <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" gap={2}>
            {successfulCheckIns > 0 && (
              <Typography variant="caption" color="success.main">
                ✓ {successfulCheckIns} successful entries
              </Typography>
            )}
            {failedCheckIns > 0 && (
              <Typography variant="caption" color="error.main">
                ⚠ {failedCheckIns} denied entries
              </Typography>
            )}
          </Box>
          <Typography variant="caption" color="text.secondary">
            {searchTerm ? `Showing ${filteredCheckIns.length} of ${checkIns.length}` : 
             `Total: ${checkIns.length}`} check-in{checkIns.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default TodaysCheckIns;
